import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Share2, Loader2, Anchor, Ship, MapPin, ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { calculateRewindStats, RewindStats } from '../utils/rewindStats';
import { shareRewindImage, generateAllRewindSlides, downloadRewindSlidesZip } from '../utils/shareUtils';
import ShareRewindImageStories from '../components/ShareRewindImageStories';
import ShareRewindTitleSlide from '../components/rewind/ShareRewindTitleSlide';
import ShareRewindOverviewSlide from '../components/rewind/ShareRewindOverviewSlide';
import ShareRewindFirstTimeShipsSlide from '../components/rewind/ShareRewindFirstTimeShipsSlide';
import ShareRewindFirstTimePortsSlide from '../components/rewind/ShareRewindFirstTimePortsSlide';
import ShareRewindShipHighlightsSlide from '../components/rewind/ShareRewindShipHighlightsSlide';
import ShareRewindCruiseLineBreakdownSlide from '../components/rewind/ShareRewindCruiseLineBreakdownSlide';
import ShareRewindShipCollageSlide from '../components/rewind/ShareRewindShipCollageSlide';
import ShareRewindClosingSlide from '../components/rewind/ShareRewindClosingSlide';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import LazyImage from '../components/LazyImage';

const Rewind = () => {
  const { year: yearParam } = useParams<{ year?: string }>();
  const navigate = useNavigate();
  const { cruises, loading } = useCruises();
  const { getShipImageUrl, getShipMetadata, loading: metadataLoading } = useCruiseMetadata();
  
  const currentYear = new Date().getFullYear();
  const year = yearParam ? parseInt(yearParam, 10) : currentYear;
  
  const [stats, setStats] = useState<RewindStats | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  
  // Share state
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [imageGenerated, setImageGenerated] = useState(false);
  const [shareOption, setShareOption] = useState<'single' | 'multi' | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // Refs for all slides
  const shareImageRef = useRef<HTMLDivElement>(null);
  const titleSlideRef = useRef<HTMLDivElement>(null);
  const overviewSlideRef = useRef<HTMLDivElement>(null);
  const firstTimeShipsSlideRef = useRef<HTMLDivElement>(null);
  const firstTimePortsSlideRef = useRef<HTMLDivElement>(null);
  const shipHighlightsSlideRef = useRef<HTMLDivElement>(null);
  const cruiseLineBreakdownSlideRef = useRef<HTMLDivElement>(null);
  const shipCollageSlideRef = useRef<HTMLDivElement>(null);
  const closingSlideRef = useRef<HTMLDivElement>(null);

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Track when data is actually loaded
  useEffect(() => {
    if (!loading && !metadataLoading && cruises) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [loading, metadataLoading, cruises]);

  // Calculate stats when cruises or year changes
  useEffect(() => {
    if (cruises && getShipImageUrl && getShipMetadata) {
      const calculatedStats = calculateRewindStats(cruises, year, getShipImageUrl, getShipMetadata);
      setStats(calculatedStats);
    }
  }, [cruises, year, getShipImageUrl, getShipMetadata]);

  // Share handlers
  const handleShare = () => {
    setImageGenerated(false);
    setShareOption(null);
    setGenerationProgress(0);
    setShowShareModal(true);
  };

  const handleGenerateSingleImage = async () => {
    if (!stats || !shareImageRef.current) return;

    try {
      setIsGeneratingShare(true);
      setShareOption('single');
      
      await shareRewindImage(shareImageRef, year);
      
      if (Capacitor.isNativePlatform()) {
        setShowShareModal(false);
      } else {
        toast.success('Rewind image downloaded!');
        setImageGenerated(true);
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      
      if (Capacitor.isNativePlatform()) {
        const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
        if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') || errorMessage.includes('user')) {
          return;
        }
      }
      
      toast.error('Failed to share Rewind image');
    } finally {
      setIsGeneratingShare(false);
      setShareOption(null);
    }
  };

  const handleGenerateMultiImage = async () => {
    if (!stats) return;

    try {
      setIsGeneratingShare(true);
      setShareOption('multi');
      setGenerationProgress(0);

      // Build slide refs and names conditionally
      const slideRefs: Array<React.RefObject<HTMLDivElement>> = [];
      const slideNames: string[] = [];

      // Always include these slides
      if (titleSlideRef.current) {
        slideRefs.push(titleSlideRef);
        slideNames.push('title');
      }
      if (overviewSlideRef.current) {
        slideRefs.push(overviewSlideRef);
        slideNames.push('overview');
      }

      // Conditionally include first time slides
      if (stats.firstTimeShips.length > 0 && firstTimeShipsSlideRef.current) {
        slideRefs.push(firstTimeShipsSlideRef);
        slideNames.push('first-time-ships');
      }
      if (stats.firstTimePorts.length > 0 && firstTimePortsSlideRef.current) {
        slideRefs.push(firstTimePortsSlideRef);
        slideNames.push('first-time-ports');
      }
      if (stats.shipHighlights && Object.keys(stats.shipHighlights).length > 0 && shipHighlightsSlideRef.current) {
        slideRefs.push(shipHighlightsSlideRef);
        slideNames.push('ship-highlights');
      }

      // Always include breakdown and collage
      if (cruiseLineBreakdownSlideRef.current) {
        slideRefs.push(cruiseLineBreakdownSlideRef);
        slideNames.push('cruise-line-breakdown');
      }
      if (shipCollageSlideRef.current) {
        slideRefs.push(shipCollageSlideRef);
        slideNames.push('ship-collage');
      }
      if (closingSlideRef.current) {
        slideRefs.push(closingSlideRef);
        slideNames.push('closing');
      }

      const slides = await generateAllRewindSlides(slideRefs, slideNames, year);
      await downloadRewindSlidesZip(slides, year);
      
      toast.success(`Downloaded ${slides.length} images! Perfect for sharing as a reel.`);
      setShowShareModal(false);
    } catch (error) {
      console.error('Error generating multi-image:', error);
      toast.error('Failed to generate slides');
    } finally {
      setIsGeneratingShare(false);
      setShareOption(null);
      setGenerationProgress(0);
    }
  };


  if (!dataLoaded || !minTimeElapsed || !stats) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/highlights');
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-50 p-4 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center">
          <button
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <div className="flex items-center justify-center">
            <CustomCruiseIcon size={64} className="text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Cruising Along Rewind {year}
          </h1>
          <p className="text-xl text-gray-600">Your cruise year in review</p>
          <p className="text-sm text-gray-500 mt-2">
            Includes all cruises in the {year} calendar year (completed and upcoming)
          </p>
        </motion.div>

        {/* Main Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-8 text-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Cruises */}
              <div className="text-center">
                <div className="text-7xl md:text-8xl font-bold mb-2">
                  {stats.cruisesCompleted}
                </div>
                <div className="text-2xl md:text-3xl font-medium">
                  {stats.cruisesCompleted === 1 ? 'Cruise' : 'Cruises'} in {year}
                </div>
              </div>

              {/* Nights at Sea */}
              <div className="text-center">
                <div className="text-7xl md:text-8xl font-bold mb-2">
                  {stats.nightsAtSea}
                </div>
                <div className="text-2xl md:text-3xl font-medium">
                  {stats.nightsAtSea === 1 ? 'Night' : 'Nights'} at Sea
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* First Time Items Grid - Only show if applicable */}
        {(stats.firstTimeCruiseLines.length > 0 || stats.firstTimeShips.length > 0 || stats.firstTimePorts.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {stats.firstTimeCruiseLines.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <Ship className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stats.firstTimeCruiseLines.length}
                </div>
                <div className="text-lg text-gray-600">
                  New Cruise {stats.firstTimeCruiseLines.length === 1 ? 'Line' : 'Lines'} Sailed
                </div>
              </div>
            )}

            {stats.firstTimeShips.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <Anchor className="h-12 w-12 text-teal-600 mx-auto mb-4" />
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stats.firstTimeShips.length}
                </div>
                <div className="text-lg text-gray-600">
                  First Time {stats.firstTimeShips.length === 1 ? 'Ship' : 'Ships'} Sailed
                </div>
              </div>
            )}

            {stats.firstTimePorts.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <MapPin className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stats.firstTimePorts.length}
                </div>
                <div className="text-lg text-gray-600">
                  First Time {stats.firstTimePorts.length === 1 ? 'Port' : 'Ports'} Visited
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Ship Collage */}
        {stats.shipImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Ships You Sailed</h2>
            <div className="grid grid-cols-3 gap-4">
              {stats.shipImages.map((ship, index) => (
                <div
                  key={index}
                  className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-blue-400 to-purple-500 relative"
                >
                  {ship.imageUrl ? (
                    <LazyImage
                      src={ship.imageUrl}
                      alt={ship.shipName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <CustomCruiseIcon size={48} className="text-white/50" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-medium truncate">{ship.shipName}</p>
                    <p className="text-white/80 text-xs truncate">{ship.cruiseLine}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Share Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <button
            onClick={handleShare}
            disabled={isGeneratingShare}
            className="flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingShare ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Share2 className="h-5 w-5 mr-2" />
                Share Your Rewind
              </>
            )}
          </button>
        </motion.div>

        {/* Share Modal */}
        {showShareModal && stats && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Share Your Rewind</h3>
              
              {isGeneratingShare ? (
                <div className="mb-6">
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="h-12 w-12 text-purple-600 animate-spin mb-4" />
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      {shareOption === 'single' ? 'Generating image...' : 'Generating images...'}
                    </p>
                    {shareOption === 'multi' && generationProgress > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-gray-600 mb-2 w-full">
                          <span>Processing slides...</span>
                          <span>{Math.round(generationProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${generationProgress}%` }}
                          />
                        </div>
                      </>
                    )}
                    {shareOption === 'single' && (
                      <p className="text-sm text-gray-600 mt-2">This may take a moment...</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Choose how you'd like to share your {year} cruise year in review!
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <button
                      onClick={handleGenerateSingleImage}
                      disabled={isGeneratingShare}
                      className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-left"
                    >
                      <Share2 className="h-5 w-5 mr-3" />
                      <div className="flex-1">
                        <div className="font-semibold">Single Image</div>
                        <div className="text-sm opacity-90">Download one shareable story image</div>
                      </div>
                    </button>

                    <button
                      onClick={handleGenerateMultiImage}
                      disabled={isGeneratingShare}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-left"
                    >
                      <Share2 className="h-5 w-5 mr-3" />
                      <div className="flex-1">
                        <div className="font-semibold">Multiple Images</div>
                        <div className="text-sm opacity-90">Download multiple images that can be shared as a reel</div>
                      </div>
                    </button>
                  </div>
                </>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    if (!isGeneratingShare) {
                      setShowShareModal(false);
                      setShareOption(null);
                      setGenerationProgress(0);
                    }
                  }}
                  disabled={isGeneratingShare}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingShare ? 'Generating...' : (shareOption ? 'Close' : 'Cancel')}
                </button>
              </div>

              {imageGenerated && !Capacitor.isNativePlatform() && shareOption === 'single' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm">
                    Image downloaded! You can now share it on your favorite social media platform.
                  </p>
                </div>
              )}
            </motion.div>
          </div>,
          document.body
        )}

        {/* Hidden Share Components - Single Image */}
        {stats && (
          <div style={{
            position: 'fixed',
            top: '-9999px',
            left: '-9999px',
            width: '1080px',
            height: '1920px',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1
          }}>
            <ShareRewindImageStories
              ref={shareImageRef}
              year={year}
              stats={stats}
            />
          </div>
        )}

        {/* Hidden Slide Components - Multi-image */}
        {stats && (
          <div style={{
            position: 'fixed',
            top: '-9999px',
            left: '-9999px',
            width: '1080px',
            height: '1920px',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: -1
          }}>
            <ShareRewindTitleSlide ref={titleSlideRef} year={year} />
            <ShareRewindOverviewSlide ref={overviewSlideRef} year={year} stats={stats} />
            {stats.firstTimeShips.length > 0 && (
              <ShareRewindFirstTimeShipsSlide ref={firstTimeShipsSlideRef} year={year} stats={stats} />
            )}
            {stats.firstTimePorts.length > 0 && (
              <ShareRewindFirstTimePortsSlide ref={firstTimePortsSlideRef} year={year} stats={stats} />
            )}
            {stats.shipHighlights && Object.keys(stats.shipHighlights).length > 0 && (
              <ShareRewindShipHighlightsSlide ref={shipHighlightsSlideRef} year={year} stats={stats} />
            )}
            <ShareRewindCruiseLineBreakdownSlide ref={cruiseLineBreakdownSlideRef} year={year} stats={stats} />
            <ShareRewindShipCollageSlide ref={shipCollageSlideRef} year={year} stats={stats} />
            <ShareRewindClosingSlide ref={closingSlideRef} year={year} />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default Rewind;

